import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { ExpandableText } from '@/components/ui/ExpandableText';
import UserSelect from '@/components/admin/common/UserSelect';
import { faArrowLeft, faCircleQuestion, faSearch, faSpinner, faTrash, faUser, faXmark } from '@/fontawesome';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { User, UserQuestion, UserQuestionID, UserQuestionStatus } from '@/models';
import type { UpdateUserQuestionRequest, UserQuestionSortField } from '@/modelsAPI';
import { USER_QUESTION_PRIORITIES, clampPriority, getUserQuestionStatusColor, getUserQuestionStatusLabel } from '@/modelsLocal';

const pageSize = 50;

type StatusFilter = UserQuestionStatus | 'ALL';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'OPEN', label: 'Offen' },
  { value: 'DONE', label: 'Erledigt' },
  { value: 'ALL', label: 'Alle' },
];

const SORTS: { value: UserQuestionSortField; label: string }[] = [
  { value: 'created', label: 'Neueste' },
  { value: 'priority', label: 'Priorität' },
];

const PILL_ACTIVE = 'bg-blue-600 text-white';
const PILL_INACTIVE = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600';

export default function AdminUserQuestionsList() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<UserQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
  const [sort, setSort] = useState<UserQuestionSortField>('created');
  const [userFilter, setUserFilter] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [updatingId, setUpdatingId] = useState<UserQuestionID | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, statusFilter, sort, userFilter, debouncedSearchTerm]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;
      const search = debouncedSearchTerm.trim();

      const response = await APIRoutes.listUserQuestions({
        limit: pageSize,
        next: cursorToken,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        userID: userFilter?.id,
        search: search === '' ? undefined : search,
        sort,
        sortDirection: 'DESC',
      });
      setQuestions(response.questions);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading user questions:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Fragen');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value: UserQuestionSortField) => {
    setSort(value);
    setCurrentPage(1);
  };

  const handleUserFilterChange = (user: User | null) => {
    setUserFilter(user);
    setCurrentPage(1);
  };

  const updateQuestion = async (question: UserQuestion, request: UpdateUserQuestionRequest, successMessage: string) => {
    try {
      setUpdatingId(question.id);
      const updated = await APIRoutes.updateUserQuestion(question.id, request);
      // Fällt die Frage aus dem aktiven Statusfilter, verschwindet sie aus der Liste
      if (statusFilter !== 'ALL' && updated.status !== statusFilter) {
        setQuestions(prev => prev.filter(q => q.id !== question.id));
        setTotal(prev => prev - 1);
      } else {
        setQuestions(prev => prev.map(q => q.id === updated.id ? { ...updated, user: updated.user ?? q.user } : q));
      }
      showSuccessToast(successMessage);
    } catch (err) {
      handleAPIError(err, 'Frage konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (question: UserQuestion) => {
    if (!confirm('Frage wirklich löschen?')) {
      return;
    }
    try {
      setUpdatingId(question.id);
      await APIRoutes.deleteUserQuestion(question.id);
      setQuestions(prev => prev.filter(q => q.id !== question.id));
      setTotal(prev => prev - 1);
      showSuccessToast('Frage gelöscht');
    } catch (err) {
      handleAPIError(err, 'Frage konnte nicht gelöscht werden.');
    } finally {
      setUpdatingId(null);
    }
  };

  if (criticalError) {
    return (
      <FullscreenError
        title="Fragen nicht verfügbar"
        message={error ?? 'Die Fragen konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchQuestions}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center mb-8">
        <Link to="/admin" className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Fragen ({total})</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Suche nach Titel oder Text..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm !== '' && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
              </button>
            )}
          </div>
          <UserSelect
            id="question-user-filter"
            selectedUser={userFilter}
            onChange={handleUserFilterChange}
            placeholder="Nach Nutzer filtern..."
          />
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Status:</span>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => handleStatusFilterChange(f.value)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${statusFilter === f.value ? PILL_ACTIVE : PILL_INACTIVE}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Sortierung:</span>
            {SORTS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => handleSortChange(s.value)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${sort === s.value ? PILL_ACTIVE : PILL_INACTIVE}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {!loading && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          {questions.length === 0 ? (
            <EmptyState icon={faCircleQuestion} title="Keine Fragen gefunden" description="Für die aktuelle Auswahl gibt es keine Fragen." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Erstellt</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priorität</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {questions.map((q) => {
                    const busy = updatingId === q.id;
                    const priority = USER_QUESTION_PRIORITIES[clampPriority(q.priority)];
                    const isDone = q.status === 'DONE';
                    return (
                      <tr key={q.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 max-w-xl">
                          <Link to={`/admin/questions/${q.id}`} className="block text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline mb-1">
                            {q.title}
                          </Link>
                          <ExpandableText text={q.body} className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/admin/users/${q.userID}`}
                            className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2" />
                            {q.user !== null ? (
                              <div>
                                <div className="text-sm font-medium">{q.user.firstName} {q.user.lastName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{q.user.email}</div>
                              </div>
                            ) : (
                              <span className="text-sm font-mono">{q.userID}</span>
                            )}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(q.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${priority.dot}`} />
                            <select
                              value={priority.value}
                              disabled={busy}
                              onChange={(e) => updateQuestion(q, { priority: Number(e.target.value) }, 'Priorität gespeichert')}
                              className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {USER_QUESTION_PRIORITIES.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col items-start gap-1">
                            <Badge color={getUserQuestionStatusColor(q.status)} title={q.completedAt !== null ? `Erledigt am ${formatDate(q.completedAt)}` : undefined}>
                              {getUserQuestionStatusLabel(q.status)}
                            </Badge>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => updateQuestion(q, { status: isDone ? 'OPEN' : 'DONE' }, isDone ? 'Frage wieder geöffnet' : 'Frage als erledigt markiert')}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDone ? 'Wieder öffnen' : 'Als erledigt markieren'}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            type="button"
                            onClick={() => handleDelete(q)}
                            disabled={busy}
                            title="Löschen"
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FontAwesomeIcon icon={busy ? faSpinner : faTrash} className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}
    </>
  );
}
